<template>
	<App title="Create Stock Record">
  
	  <div class="container">
  
		<!-- Page Header -->
		<div class="page-header mb-2">
			<a :href="route('stock.page.group')">
				<div class="d-flex align-items-center gap-2 back-link mb-3">
					<i class="fa fa-arrow-left fa-xs"></i>
					<span>Back to stock</span>
				</div>
			</a>
		  <div class="d-flex align-items-center justify-content-between flex-wrap gap-3">
			<h1 class="page-title">Create Stock Record</h1>
			<div class="tab-toggle">
			  <button
				class="toggle-btn"
				:class="{ 'bg-secondary text-light': activeTab === 'single' }"
				@click="activeTab = 'single'"
			  >
				<i class="fa fa-pen fa-xs me-2"></i>Single Entry
			  </button>
			  <button
				class="toggle-btn  "
				:class="{ 'bg-secondary text-light': activeTab === 'bulk' }"
				@click="activeTab = 'bulk'"
			  >
				<i class="fa fa-layer-group fa-xs me-2"></i>Bulk Import
			  </button>
			</div>
		  </div>
		</div>
  
		<!-- Single Entry Panel -->
		<transition name="panel-fade" mode="out-in">
		  <div v-if="activeTab === 'single'" key="single" class="panel-card rounded-0 border">
			<div class="panel-section-label">
			  <span class="section-dot"></span>
			  Stock Information
			</div>
  
			<Form ref="form" @submit="onSubmit">
			  <div class="row g-4">
  
				<div class="col-12">
				  <label class="field-label" for="serial_number">Serial Number</label>
				  <InputField
					id="serial_number"
					v-model="serial"
					name="serial"
					rules="required"
					label="Seria number e.g. ENG-2024-00142"
					class=" rounded-0"
				  />
				</div>
  
				<div class="col-12 col-md-6">
				  <label class="field-label" for="type">Stock Type</label>
				  <SelectField
					id="type"
					v-model="type"
					name="type"
					rules="required"
					label=""
					:options="type_options"
				  >
					<option v-for="t in types" :key="t.name" :value="t.value">{{ t.name }}</option>
				  </SelectField>
				</div>
  
				<div class="col-12 col-md-6">
				  <label class="field-label" for="warehouse">Warehouse</label>
				  <SelectField
					id="warehouse"
					v-model="warehouse"
					name="warehouse"
					rules="required"
					label=""
					:options="warehouse_options"
					:disabled="!isSuperAdmin"
				  />
				</div>
  
				<div class="col-12">
				  <div class="divider"></div>
				</div>
  
				<div class="col-12">
				  <div class="d-flex justify-content-end gap-2">
					<button type="button" class="btn bg-danger text-light" @click="goBack">Cancel</button>
					<Button
					  id="action"
					  type="submit"
					  :label="label"
					  :loading="loading"
					  :done="done"
					  class="btn bg-secondary text-light"
					/>
				  </div>
				</div>
  
			  </div>
			</Form>
		  </div>
  
		  <!-- Bulk Import Panel -->
		  <div v-else key="bulk" class="panel-card rounded-0">
			<div class="panel-section-label">
			  <span class="section-dot"></span>
			  Bulk Import
			</div>
  
			<div class="row g-4">
			  <div class="col-12">
				<Dropzone
				  id="dropzone"
				  name="file"
				  label="Upload Data Sheet file"
				  :url="route('stock.ajax.import')"
				  @success="onImportSuccess"
				  @error="onImportError"
				  param="file"
				  class="modern-dropzone"
				/>
			  </div>
  
			  <div class="col-12">
				<div class="import-hint">
				  <i class="fa fa-circle-info fa-xs me-2 text-muted"></i>
				  <span>Upload a <strong>.xlsx</strong> or <strong>.csv</strong> file. Ensure it matches the template format.</span>
				</div>
			  </div>
  
			  <div class="col-12">
				<div class="divider"></div>
			  </div>
  
			  <div class="col-12 d-flex justify-content-end">
				<a :href="route('stock.ajax.download')" class="btn-outline-download">
				  <i class="fa fa-download fa-xs me-2"></i>Download Template
				</a>
			  </div>
			</div>
		  </div>
		</transition>
  
	  </div>
  
	  <!-- Notifications -->
	  <div class="w-100 position-relative">
		<div class="mt-9 position-absolute top-0 end-0">
		  <Notivue v-slot="item">
			<Notification :item="item" :theme="materialTheme">
			  <NotificationProgress :item="item" />
			</Notification>
		  </Notivue>
		</div>
	  </div>
  
	</App>
  </template>
  
  <script lang="ts">
  import Alert from '@/vue/components/Alert.vue'
  import Block from '@/vue/components/Block.vue'
  import Button from '@/vue/components/Button.vue'
  import Form from '@/vue/components/Form.vue'
  import InputField from '@/vue/components/InputField.vue'
  import SelectField from '@/vue/components/SelectField.vue'
  import { Notivue, Notification, push, materialTheme, NotificationProgress } from 'notivue'
  import $ from 'jquery'
  import { Options as Select2Options } from 'select2'
  import Dropzone from '@/widget/Dropzone.vue'
  import App from '@/vue/templates/App.vue'
  import Swal from 'sweetalert2'
  import { ButtonLabel } from '@/vue/utils/Button'
  import { Component, Ref, toNative, Vue, Watch, Prop } from 'vue-facing-decorator'
  
  @Component({
	components: {
	  Block,
	  Form,
	  InputField,
	  Button,
	  Alert,
	  App,
	  Notivue,
	  Notification,
	  NotificationProgress,
	  SelectField,
	  Dropzone,
	},
  })
  class Create extends Vue {
	loading = false
	done = false
	message = ''
	type = ''
	warehouse = this.$user?.workplace?.slug ?? ''
	serial = ''
	status = 'none'
	activeTab: 'single' | 'bulk' = 'single'
	materialTheme = materialTheme
  
	@Ref('form')
	readonly form!: InstanceType<typeof Form>
  
	@Ref('alert')
	readonly alert!: InstanceType<typeof Alert>
  
	@Prop({ type: Array })
	readonly types!: Array<{ name: string; value: string }>
  
	@Watch('message')
	onMessageChanged(value: string) {
	  if (value.length > 0) {
		this.alert.show()
	  } else {
		this.alert.hide()
	  }
	}
	get isSuperAdmin(){
        return this.$user?.role === "super_admin"
    }
	get label(): ButtonLabel {
	  return {
		normal: 'Create Stock',
		loading: 'Creating…',
		done: 'Stock Created',
	  }
	}
  
	get type_options(): Select2Options {
	  return {
		placeholder: 'Select Stock Type',
		minimumResultsForSearch: Infinity,
		//@ts-ignore
		ajax: null,
	  }
	}
  
	get warehouse_options(): Select2Options {
	  return {
		placeholder: 'Select Warehouse',
		minimumResultsForSearch: Infinity,
		ajax: {
		  method: 'get',
		  url: this.route('warehouse.ajax.select.warehouse'),
		},
		data: !this.isSuperAdmin ? [{
			id: this.$user?.workplace?.slug,
			text: `${this.$user?.workplace?.title}`,
			title: `${this.$user?.workplace?.title}`,
			selected: true,
			}]:[],
		//@ts-ignore
		templateResult: ({ id, title, text }) => (id ? $(`<span>${title}</span>`) : text),
		//@ts-ignore
		templateSelection: ({ id, title, text }) => (id ? $(`<span>${title}</span>`) : text),
	  }
	}
  
	onSubmit(payload) {
	  this.loading = true
	  this.$http
		.post(this.route('stock.ajax.create'), payload)
		.then((response) => {
		  this.done = true
		  this.message = response?.data?.message || 'Stock created successfully.'
		  push.success({ title: 'Success', message: this.message })
		  setTimeout(() => window.history.back(), 3000)
		})
		.catch((e) => {
		  this.message = e.response?.data?.message || 'An error occurred.'
		  push.error({ title: 'Error', message: this.message })
		})
		.finally(() => (this.loading = false))
	}

	showToast(message: string, type: 'success' | 'error' ) {
        
        Swal.fire({
            toast: true,
            position: 'top-end', 
            icon: type, 
            title: message,
            showConfirmButton: false, 
            timer: 5000, 
            timerProgressBar: true,
            didOpen: (toast) => {
                // Pause the timer when the mouse hovers over the toast
                toast.addEventListener('mouseenter', Swal.stopTimer)
                // Resume the timer when the mouse leaves
                toast.addEventListener('mouseleave', Swal.resumeTimer)
            }
        })
    }
	created() {
		if (!this.isSuperAdmin) {
			this.warehouse = this.$user?.workplace?.slug ?? ''
		}
	}
	onImportSuccess(response) {
	  const message = response.xhr?.response ? JSON.parse(response.xhr.response).message : 'Data imported successfully!'
	  this.showToast(message, 'success')
	  setTimeout(() => window.history.back(), 3000)
	}
  
	onImportError(error) {
	  const message = error.xhr?.response ? JSON.parse(error.xhr.response).message : 'An error occurred during import. Please contact support.'
	  this.showToast(message, 'error')
	}

	goBack() {
		window.history.back()
	}
  }

  export default toNative(Create)
  </script>
  
  <style scoped>
  /* ─── Page Shell ─────────────────────────────────────── */
  .stock-page {
	max-width: 680px;
	margin: 0 auto;
	padding: 2rem 1rem;
	font-family: 'DM Sans', 'Figtree', sans-serif;
  }
  
  /* ─── Header ─────────────────────────────────────────── */
  .back-link {
	font-size: 0.8rem;
	color: #9ca3af;
	cursor: pointer;
	width: fit-content;
	transition: color 0.2s;
  }
  .back-link:hover { color: #374151; }
  
  .page-title {
	font-size: 1.75rem;
	font-weight: 700;
	letter-spacing: -0.03em;
	color: #111827;
	margin: 0;
  }
  
  /* ─── Tab Toggle ─────────────────────────────────────── */
  .tab-toggle {
	display: flex;
	background: #f3f4f6;
	border-radius: 10px;
	padding: 4px;
	gap: 2px;
  }
  
  .toggle-btn {
	padding: 0.4rem 1.1rem;
	font-size: 0.82rem;
	font-weight: 500;
	border: none;
	border-radius: 7px;
	background: transparent;
	color: #6b7280;
	cursor: pointer;
	transition: all 0.22s ease;
	white-space: nowrap;
  }
  
  .toggle-btn.active {
	background: #111827;
	color: #ffffff;
	box-shadow: 0 1px 4px rgba(0, 0, 0, 0.18);
  }
  
  .toggle-btn:not(.active):hover {
	background: #e5e7eb;
	color: #374151;
  }
  
  /* ─── Panel Card ─────────────────────────────────────── */
  .panel-card {
	background: #ffffff;
	border: 1px solid #e5e7eb;
	border-radius: 16px;
	padding: 2rem 2rem 1.75rem;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 4px 16px rgba(0, 0, 0, 0.04);
  }
  
  /* ─── Section Label ──────────────────────────────────── */
  .panel-section-label {
	display: flex;
	align-items: center;
	gap: 0.5rem;
	font-size: 0.72rem;
	font-weight: 700;
	letter-spacing: 0.1em;
	text-transform: uppercase;
	color: #9ca3af;
	margin-bottom: 1.75rem;
  }
  
  .section-dot {
	display: inline-block;
	width: 6px;
	height: 6px;
	border-radius: 50%;
	background: #111827;
	flex-shrink: 0;
  }
  
  /* ─── Field Labels ───────────────────────────────────── */
  .field-label {
	display: block;
	font-size: 0.8rem;
	font-weight: 600;
	color: #374151;
	margin-bottom: 0.45rem;
	letter-spacing: -0.01em;
  }
  
  /* ─── Divider ────────────────────────────────────────── */
  .divider {
	height: 1px;
	background: #f3f4f6;
  }
  
  /* ─── Buttons ────────────────────────────────────────── */
  .btn-flat-cancel {
	padding: 0.55rem 1.25rem;
	font-size: 0.84rem;
	font-weight: 500;
	border: 1px solid #e5e7eb;
	border-radius: 9px;
	background: transparent;
	color: #6b7280;
	cursor: pointer;
	transition: all 0.2s;
  }
  
  .btn-flat-cancel:hover {
	background: #f9fafb;
	color: #374151;
	border-color: #d1d5db;
  }
  
  :deep(.btn-primary-dark),
  :deep(.btn-primary-dark button) {
	padding: 0.55rem 1.5rem;
	font-size: 0.84rem;
	font-weight: 600;
	border-radius: 9px;
	background: #111827 !important;
	color: #ffffff !important;
	border: none;
	cursor: pointer;
	transition: background 0.2s, transform 0.15s;
	letter-spacing: -0.01em;
  }
  
  :deep(.btn-primary-dark:hover),
  :deep(.btn-primary-dark button:hover) {
	background: #1f2937 !important;
	transform: translateY(-1px);
  }
  
  /* ─── Import Hint ────────────────────────────────────── */
  .import-hint {
	font-size: 0.82rem;
	color: #6b7280;
	background: #f9fafb;
	border: 1px solid #f3f4f6;
	border-radius: 8px;
	padding: 0.65rem 0.9rem;
	line-height: 1.5;
  }
  
  /* ─── Download Button ────────────────────────────────── */
  .btn-outline-download {
	display: inline-flex;
	align-items: center;
	padding: 0.55rem 1.25rem;
	font-size: 0.84rem;
	font-weight: 600;
	border: 1.5px solid #111827;
	border-radius: 9px;
	color: #111827;
	text-decoration: none;
	background: transparent;
	transition: all 0.2s;
	letter-spacing: -0.01em;
  }
  
  .btn-outline-download:hover {
	background: #111827;
	color: #fff;
  }
  
  /* ─── Panel Transition ───────────────────────────────── */
  .panel-fade-enter-active,
  .panel-fade-leave-active {
	transition: opacity 0.18s ease, transform 0.2s ease;
  }
  .panel-fade-enter-from {
	opacity: 0;
	transform: translateY(8px);
  }
  .panel-fade-leave-to {
	opacity: 0;
	transform: translateY(-6px);
  }
  
  /* ─── Input overrides (target wrapped components) ───── */
  :deep(input.form-control),
  :deep(.form-control) {
	border: 1.5px solid #e5e7eb !important;
	border-radius: 9px !important;
	padding: 0.55rem 0.85rem !important;
	font-size: 0.875rem !important;
	color: #111827 !important;
	background: #fafafa !important;
	transition: border-color 0.2s, box-shadow 0.2s !important;
	box-shadow: none !important;
  }
  
  :deep(input.form-control:focus),
  :deep(.form-control:focus) {
	border-color: #111827 !important;
	background: #fff !important;
	box-shadow: 0 0 0 3px rgba(17, 24, 39, 0.07) !important;
	outline: none !important;
  }
  
  :deep(.select2-container .select2-selection--single) {
	border: 1.5px solid #e5e7eb !important;
	border-radius: 9px !important;
	height: 40px !important;
	background: #fafafa !important;
  }
  
  :deep(.select2-container--default .select2-selection--single .select2-selection__rendered) {
	line-height: 38px !important;
	font-size: 0.875rem !important;
	color: #374151 !important;
	padding-left: 0.85rem !important;
  }
  
  :deep(.select2-container--default.select2-container--focus .select2-selection--single),
  :deep(.select2-container--default.select2-container--open .select2-selection--single) {
	border-color: #111827 !important;
	box-shadow: 0 0 0 3px rgba(17, 24, 39, 0.07) !important;
  }
  
  /* ─── Hide default form label duplication ────────────── */
  :deep(.form-group > label:first-child) {
	display: none;
  }
  
  /* ─── Dropzone override ──────────────────────────────── */
  :deep(.dz-message) {
	border: 2px dashed #e5e7eb !important;
	border-radius: 12px !important;
	background: #f9fafb !important;
	transition: border-color 0.2s, background 0.2s;
  }
  
  :deep(.dz-message:hover) {
	border-color: #9ca3af !important;
	background: #f3f4f6 !important;
  }
  
  /* ─── Responsive ─────────────────────────────────────── */
  @media (max-width: 576px) {
	.panel-card { padding: 1.5rem 1.25rem; }
	.page-title { font-size: 1.4rem; }
	.tab-toggle { width: 100%; }
	.toggle-btn { flex: 1; text-align: center; }
  }
  </style>